/**
 * ConversationFilterPanel — reusable filter card extracted from
 * ConversationSearchDrawer. Renders the full set of conversation filters
 * (classification, routing, organization, agents, details) in a white card
 * with Reset + Apply actions.
 *
 * Used by:
 *  - ConversationSearchDrawer (inside the search drawer, above results)
 *  - ConversationInbox (inside the advanced-filter drawer)
 */
import { Fragment, useMemo, useState, useEffect, type JSX, type ReactNode } from 'react'
import { Search, RotateCcw, Filter, UserCog, SlidersHorizontal } from 'lucide-react'
import { Field, SelectField, R } from './detail/primitives.js'
import DualListSelect, { type DualListOption } from '../common/DualListSelect.js'
import FilterFieldsDialog from './FilterFieldsDialog.js'
import { FILTER_FIELDS, FILTER_GROUPS, type FilterFieldKey } from './filterFields.js'
import { useConversationFilterFields } from '../../hooks/useConversationFilterFields.js'
import { useCampaigns } from '../../hooks/useCampaigns.js'
import { useTeams } from '../../hooks/useTeams.js'
import { useAgentUsers } from '../../hooks/useAgentUsers.js'
import { usePriorities } from '../../hooks/usePriorities.js'
import { useCategories } from '../../hooks/useCategories.js'
import { useSubcategories } from '../../hooks/useSubcategories.js'
import { useTypes } from '../../hooks/useTypes.js'
import { useImpacts } from '../../hooks/useImpacts.js'
import { useUrgencies } from '../../hooks/useUrgencies.js'
import { useComplexities } from '../../hooks/useComplexities.js'
import { useLocations } from '../../hooks/useLocations.js'
import { useBranches } from '../../hooks/useBranches.js'
import { useDepartments } from '../../hooks/useDepartments.js'
import { useStates } from '../../hooks/useStates.js'
import { useApprovalStates } from '../../hooks/useApprovalStates.js'
import { useApprovalLevels } from '../../hooks/useApprovalLevels.js'
import { useAdditionalProperties } from '../../hooks/useAdditionalProperties.js'
import { useClientOptions } from '../../hooks/useClientOptions.js'
import { useConversationStatuses } from '../../hooks/useConversationStatuses.js'
import DatePicker from '../ui/DatePicker.js'

/** The four lifecycle positions a conversation can sit in. Tenant-defined
 *  statuses live in `conversation_status` and are loaded from there — this is
 *  the coarse "where in its life is this conversation" filter that sits beside them. */
const LIFECYCLE_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'reopen', label: 'Reopened' },
  { value: 'closed', label: 'Closed' },
]

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'chat', label: 'Chat' },
  { value: 'voice', label: 'Voice' },
  { value: 'web', label: 'Web' },
]

export interface ConversationFilterState {
  keyword: string
  referenceId: string
  clientId: string
  subject: string
  description: string
  /** Lifecycle enum (open/on_hold/reopen/closed). */
  status: string
  /** Row in the tenant's `conversation_status` catalog. */
  conversationStatusId: string
  channel: string
  typeId: string
  categoryId: string
  subcategoryId: string
  priorityId: string
  impactId: string
  urgencyId: string
  complexityId: string
  campaignId: string
  teamId: string
  /** Bypass the campaign/team narrowing and list every agent in the tenant. */
  allAgents: boolean
  agentIds: string[]
  locationId: string
  branchId: string
  departmentId: string
  stateId: string
  approvalStateId: string
  approvalLevelId: string
  dynamicProperty: string
  dynamicPropertyValue: string
  from: string
  to: string
}

export const EMPTY_FILTER_STATE: ConversationFilterState = {
  keyword: '', referenceId: '', clientId: '', subject: '', description: '', status: '', conversationStatusId: '', channel: '',
  typeId: '', categoryId: '', subcategoryId: '', priorityId: '',
  impactId: '', urgencyId: '', complexityId: '',
  campaignId: '', teamId: '', agentIds: [], allAgents: false,
  locationId: '', branchId: '', departmentId: '',
  stateId: '', approvalStateId: '', approvalLevelId: '',
  dynamicProperty: '', dynamicPropertyValue: '',
  from: '', to: '',
}

export type FilterChange = Partial<ConversationFilterState>

export interface ConversationFilterPanelProps {
  state: ConversationFilterState
  onChange: (change: FilterChange) => void
  onReset: () => void
  /** Optional apply callback (e.g. "Search" in the drawer, "Apply" in the inbox). */
  onApply?: () => void
  applyLabel?: string
  applying?: boolean
  /** Show the keyword search input at the top (default true). */
  showKeyword?: boolean
  /** Show the channel dropdown (default true). */
  showChannel?: boolean
  /** Show the date range fields (default true). */
  showDateRange?: boolean
  /** Render the panel's own Reset/Apply bar. Hosts that supply their own
   *  footer (the inbox drawer) pass false — otherwise the user sees two
   *  reset buttons and has to guess which one clears what. */
  showActions?: boolean
}

/* ── Small labelled group heading inside the filter grid ── */
/** `id` lets the filter guide spotlight a group by its heading. */
export function filterGroupAnchorId(group: string): string {
  return `filter-group-${group.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
}

function GroupLabel({ children }: { children: string }): JSX.Element {
  return (
    <div id={filterGroupAnchorId(children)} className="col-span-full mt-1 flex items-center gap-2 first:mt-0">
      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{children}</span>
      <span className="h-px flex-1 bg-slate-100" />
    </div>
  )
}

/* Shared text-input chrome — every free-text filter uses the same one. */
const INPUT = `w-full ${R} border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition-colors focus:border-[#3366FF] focus:bg-white focus:ring-2 focus:ring-[#EEF4FF]`

export default function ConversationFilterPanel({
  state,
  onChange,
  onReset,
  onApply,
  applyLabel = 'Search',
  applying = false,
  showKeyword = true,
  showChannel = true,
  showDateRange = true,
  showActions = true,
}: ConversationFilterPanelProps): JSX.Element {
  const { campaigns } = useCampaigns()
  const { teams } = useTeams(true)
  const { users: agentUsers } = useAgentUsers(true)
  const { priorities } = usePriorities(true)
  const { categories } = useCategories(true)
  const { subcategories } = useSubcategories(state.categoryId || undefined)
  const { types } = useTypes(true)
  const { impacts } = useImpacts(true)
  const { urgencies } = useUrgencies(true)
  const { complexities } = useComplexities(true)
  const { locations } = useLocations()
  const { branches } = useBranches(state.locationId || undefined)
  const { departments } = useDepartments(state.branchId || undefined)
  const { states } = useStates(true)
  const { approvalStates } = useApprovalStates()
  const { approvalLevels } = useApprovalLevels()
  const { properties: dynamicProperties } = useAdditionalProperties(true)
  const { clients } = useClientOptions()
  const { statuses, loading: statusesLoading } = useConversationStatuses()

  /* ── Which fields this user keeps on the form ──
   * Per-user preference (users.conversation_filter_fields). The show* props
   * stay hard gates on top of it: a surface that has no keyword box never
   * renders one, whatever the user ticked. */
  const { fields: selectedFields, save: saveFields, reset: resetFields } = useConversationFilterFields()
  const [fieldsDialogOpen, setFieldsDialogOpen] = useState(false)
  const selected = useMemo(() => new Set<FilterFieldKey>(selectedFields), [selectedFields])
  const omittedFields = useMemo<FilterFieldKey[]>(() => {
    const out: FilterFieldKey[] = []
    if (!showKeyword) out.push('keyword')
    if (!showChannel) out.push('channel')
    if (!showDateRange) out.push('dateRange')
    return out
  }, [showKeyword, showChannel, showDateRange])
  const shows = (key: FilterFieldKey): boolean =>
    selected.has(key) && !omittedFields.includes(key)

  // Fetch dropdown options for the selected dynamic property
  const [dynamicPropertyOptions, setDynamicPropertyOptions] = useState<{ value: string; label: string }[]>([])
  useEffect(() => {
    if (!state.dynamicProperty) { setDynamicPropertyOptions([]); return }
    const prop = dynamicProperties.find((p) => p.columnName === state.dynamicProperty)
    if (prop && prop.options.length > 0) {
      setDynamicPropertyOptions(prop.options)
    } else {
      setDynamicPropertyOptions([])
    }
  }, [state.dynamicProperty, dynamicProperties])

  const dynamicPropertyDropdown = dynamicProperties.map((p) => ({ value: p.columnName, label: p.displayName }))

  const opt = (rows: { id: string; name: string }[]) => rows.map((r) => ({ value: r.id, label: r.name }))
  const campaignOptions = useMemo(() => opt(campaigns), [campaigns])
  // Statuses come from the tenant's catalog, so a status added in admin is
  // filterable here without a code change.
  const statusOptions = useMemo(
    () => statuses.map((s) => ({ value: s.id, label: s.name })),
    [statuses],
  )
  const teamOptions = useMemo(
    () => teams.filter((t) => !state.campaignId || String(t.campaignId ?? '') === state.campaignId).map((t) => ({ value: t.id, label: t.name })),
    [teams, state.campaignId],
  )
  /**
   * Campaign and Team scope the agent list, so they are required unless the
   * agent picker has been taken off that leash with "Show all agents". Only
   * enforced while those two fields are actually on the form — otherwise the
   * requirement could never be satisfied and Apply would stay disabled.
   */
  const agentScopeMissing =
    shows('agents') && shows('campaignId') && shows('teamId')
    && !state.allAgents && (!state.campaignId || !state.teamId)

  // Agents are normally narrowed by campaign (then team) because that is how
  // they are organised. `allAgents` is the escape hatch for the common case of
  // knowing who you want but not which campaign they sit in — it lists every
  // agent in the tenant, unfiltered.
  //
  // The option value is `agentId` (agents.id), NOT the user id: the filter is
  // applied against conversations.assigned_agent_id.
  //
  // A user who took Campaign off their form has no way to scope the list, so
  // the picker lists everyone — same as ticking "Show all agents".
  const agentsUnscoped = state.allAgents || !shows('campaignId')
  const agentOptions = useMemo<DualListOption[]>(
    () => {
      const assignable = agentUsers.filter((u) => u.agentId)
      const eligible = agentsUnscoped
        ? assignable
        : !state.campaignId
          ? []
          : assignable.filter((u) =>
              (u.campaignIds?.includes(state.campaignId) || String(u.campaignId ?? '') === state.campaignId)
              && (!state.teamId || u.teamIds?.includes(state.teamId) || String(u.teamId ?? '') === state.teamId))
      return eligible
        .map((u) => ({
          value: u.agentId as string,
          label: u.loginid || u.name || 'Agent',
          sublabel: u.loginid && u.name && u.name !== u.loginid ? u.name : undefined,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    [agentUsers, state.campaignId, state.teamId, agentsUnscoped],
  )

  /* Every grid-placed control, keyed so the group loop below can lay them out
   * in catalog order. `keyword` and `agents` are not here — they render outside
   * the grid (full-width row and dual-list block). */
  const fieldNodes: Partial<Record<FilterFieldKey, ReactNode>> = {
    referenceId: (
      <Field label="Conversation ID">
        <input value={state.referenceId} onChange={(e) => onChange({ referenceId: e.target.value })} placeholder="e.g. TKT-2026-000007" className={INPUT} />
      </Field>
    ),
    subject: (
      <Field label="Subject (Case Sensitive)">
        <input value={state.subject} onChange={(e) => onChange({ subject: e.target.value })} placeholder="Enter subject…" className={INPUT} />
      </Field>
    ),
    description: (
      <Field label="Description (Case Sensitive)">
        <input value={state.description} onChange={(e) => onChange({ description: e.target.value })} placeholder="Enter description…" className={INPUT} />
      </Field>
    ),

    clientId: (
      <Field label="Client">
        <SelectField value={state.clientId} onChange={(v) => onChange({ clientId: v })} placeholder="Any client" options={opt(clients)} allowClear />
      </Field>
    ),
    locationId: (
      <Field label="Location">
        <SelectField
          value={state.locationId}
          onChange={(v) => onChange({ locationId: v, branchId: '', departmentId: '' })}
          placeholder="Any location"
          options={opt(locations)}
          allowClear
        />
      </Field>
    ),
    branchId: (
      <Field label="Branch">
        <SelectField value={state.branchId} onChange={(v) => onChange({ branchId: v, departmentId: '' })} disabled={!state.locationId} placeholder={state.locationId ? 'Any branch' : 'Select a location first'} options={opt(branches)} allowClear />
      </Field>
    ),
    departmentId: (
      <Field label="Department">
        <SelectField value={state.departmentId} onChange={(v) => onChange({ departmentId: v })} disabled={!state.branchId} placeholder={state.branchId ? 'Any department' : 'Select a branch first'} options={opt(departments)} allowClear />
      </Field>
    ),

    campaignId: (
      <Field label="Campaign" required={!state.allAgents && shows('agents')}>
        <SelectField
          value={state.campaignId}
          onChange={(v) => onChange({ campaignId: v, teamId: '', agentIds: [] })}
          placeholder="Any campaign"
          options={campaignOptions}
          allowClear
        />
      </Field>
    ),
    teamId: (
      <Field label="Team" required={!state.allAgents && shows('agents')}>
        <SelectField value={state.teamId} onChange={(v) => onChange({ teamId: v, agentIds: [] })} disabled={!state.campaignId} placeholder={state.campaignId ? 'Any team' : 'Select a campaign first'} options={teamOptions} allowClear />
      </Field>
    ),

    dateRange: (
      <>
        <Field label="Created From">
                    <DatePicker
            ariaLabel="Created From"
            size="sm"
            value={state.from}
            onChange={(v) => onChange({ from: v })}
          />
        </Field>
        <Field label="Created Upto">
                    <DatePicker
            ariaLabel="Created Upto"
            size="sm"
            value={state.to}
            onChange={(v) => onChange({ to: v })}
          />
        </Field>
      </>
    ),

    channel: (
      <Field label="Channel/Source">
        <SelectField value={state.channel} onChange={(v) => onChange({ channel: v })} placeholder="Any channel" options={CHANNEL_OPTIONS} allowClear />
      </Field>
    ),
    typeId: (
      <Field label="Type"><SelectField value={state.typeId} onChange={(v) => onChange({ typeId: v })} placeholder="Any type" options={opt(types)} allowClear /></Field>
    ),
    conversationStatusId: (
      <Field label="Status">
        <SelectField
          value={state.conversationStatusId}
          onChange={(v) => onChange({ conversationStatusId: v })}
          placeholder={statusesLoading ? 'Loading statuses…' : 'Any status'}
          options={statusOptions}
          allowClear
        />
      </Field>
    ),
    status: (
      <Field label="Lifecycle"><SelectField value={state.status} onChange={(v) => onChange({ status: v })} placeholder="Any lifecycle" options={LIFECYCLE_OPTIONS} allowClear /></Field>
    ),
    stateId: (
      <Field label="State"><SelectField value={state.stateId} onChange={(v) => onChange({ stateId: v })} placeholder="Any state" options={opt(states)} allowClear /></Field>
    ),
    categoryId: (
      <Field label="Category">
        <SelectField
          value={state.categoryId}
          onChange={(v) => onChange({ categoryId: v, subcategoryId: '' })}
          placeholder="Any category"
          options={opt(categories)}
          allowClear
        />
      </Field>
    ),
    subcategoryId: (
      <Field label="Sub Category">
        <SelectField value={state.subcategoryId} onChange={(v) => onChange({ subcategoryId: v })} disabled={!state.categoryId} placeholder={state.categoryId ? 'Any sub category' : 'Select a category first'} options={opt(subcategories)} allowClear />
      </Field>
    ),
    impactId: (
      <Field label="Impact"><SelectField value={state.impactId} onChange={(v) => onChange({ impactId: v })} placeholder="Any impact" options={opt(impacts)} allowClear /></Field>
    ),
    urgencyId: (
      <Field label="Urgency"><SelectField value={state.urgencyId} onChange={(v) => onChange({ urgencyId: v })} placeholder="Any urgency" options={opt(urgencies)} allowClear /></Field>
    ),
    priorityId: (
      <Field label="Priority"><SelectField value={state.priorityId} onChange={(v) => onChange({ priorityId: v })} placeholder="Any priority" options={opt(priorities)} allowClear /></Field>
    ),
    complexityId: (
      <Field label="Complexity"><SelectField value={state.complexityId} onChange={(v) => onChange({ complexityId: v })} placeholder="Any complexity" options={opt(complexities)} allowClear /></Field>
    ),

    approvalStateId: (
      <Field label="Approval State"><SelectField value={state.approvalStateId} onChange={(v) => onChange({ approvalStateId: v })} placeholder="Any approval state" options={opt(approvalStates)} allowClear /></Field>
    ),
    approvalLevelId: (
      <Field label="Approval Level"><SelectField value={state.approvalLevelId} onChange={(v) => onChange({ approvalLevelId: v })} placeholder="Any approval level" options={opt(approvalLevels)} allowClear /></Field>
    ),

    dynamicProperty: (
      <>
        <Field label="Additional Property">
          <SelectField
            value={state.dynamicProperty}
            onChange={(v) => onChange({ dynamicProperty: v, dynamicPropertyValue: '' })}
            placeholder="Select property"
            options={dynamicPropertyDropdown}
            allowClear
          />
        </Field>
        {state.dynamicProperty && (
          <Field label="Property Value (Case Sensitive)">
            {dynamicPropertyOptions.length > 0 ? (
              <SelectField
                value={state.dynamicPropertyValue}
                onChange={(v) => onChange({ dynamicPropertyValue: v })}
                placeholder="Select value"
                options={dynamicPropertyOptions}
                allowClear
              />
            ) : (
              <input value={state.dynamicPropertyValue} onChange={(e) => onChange({ dynamicPropertyValue: e.target.value })} placeholder="Enter value…" className={INPUT} />
            )}
          </Field>
        )}
      </>
    ),
  }

  // Only groups with at least one chosen field get a heading — no stray
  // "Approval" rule above an empty row.
  const groupSections = FILTER_GROUPS
    .map((group) => ({
      group,
      keys: FILTER_FIELDS
        .filter((f) => f.group === group && shows(f.key) && fieldNodes[f.key] !== undefined)
        .map((f) => f.key),
    }))
    .filter((g) => g.keys.length > 0)

  const showAgents = shows('agents')
  const nothingSelected = groupSections.length === 0 && !showAgents && !shows('keyword')

  return (
    <div className={`${R} border border-slate-200 bg-white shadow-sm`}>
      <div className="flex items-center gap-2.5 border-b border-slate-100 px-5 py-3">
        <span className={`flex h-7 w-7 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}>
          <Filter className="h-3.5 w-3.5" />
        </span>
        <h3 className="text-sm font-bold text-slate-800">Filters</h3>
        <button
          type="button"
          id="filter-fields-button"
          onClick={() => setFieldsDialogOpen(true)}
          className={`ml-auto inline-flex items-center gap-1.5 ${R} border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50`}
          title="Choose which filter fields appear on this form"
        >
          <SlidersHorizontal className="h-3.5 w-3.5 text-slate-400" />
          Fields
        </button>
      </div>

      <div className="p-5">
        {/* Keyword row */}
        {shows('keyword') && (
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={state.keyword}
              onChange={(e) => onChange({ keyword: e.target.value })}
              placeholder="Search by keyword — subject, name, handle or tag…"
              className={`w-full ${R} border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none transition-colors focus:border-[#3366FF] focus:bg-white focus:ring-2 focus:ring-[#EEF4FF]`}
            />
          </div>
        )}

        {nothingSelected ? (
          <div className={`flex flex-col items-center gap-2 ${R} border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center`}>
            <SlidersHorizontal className="h-5 w-5 text-slate-300" />
            <p className="text-[13px] font-semibold text-slate-600">No filter fields on your form</p>
            <p className="text-[12px] text-slate-400">Choose the filters you want to see — your selection is saved to your profile.</p>
            <button
              type="button"
              onClick={() => setFieldsDialogOpen(true)}
              className={`mt-1 ${R} bg-[#3366FF] px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC]`}
            >
              Choose fields
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            {groupSections.map(({ group, keys }) => (
              <Fragment key={group}>
                <GroupLabel>{group}</GroupLabel>
                {keys.map((key) => (
                  <Fragment key={key}>{fieldNodes[key]}</Fragment>
                ))}
              </Fragment>
            ))}
          </div>
        )}

        {/* Agents — dual-list pick list (mirrors the KC2 PickList) */}
        {showAgents && (
          <div id="filter-agents" className="mt-4">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <UserCog className="h-3.5 w-3.5 text-[#3366FF]" />
              <span className="text-xs font-semibold text-slate-600">Agents</span>
              {state.agentIds.length > 0 && (
                <span className={`${R} bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-[#3366FF]`}>{state.agentIds.length}</span>
              )}
              {!agentsUnscoped && (
                <span className="text-[11px] text-slate-400">— filtered by campaign{state.teamId ? ' & team' : ''}</span>
              )}
              {shows('campaignId') && (
                <label
                  className={`ml-auto inline-flex cursor-pointer select-none items-center gap-2 ${R} border px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
                    state.allAgents
                      ? 'border-[#3366FF] bg-blue-50 text-[#3366FF]'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={state.allAgents}
                    onChange={(e) => onChange({ allAgents: e.target.checked, agentIds: [] })}
                    className="h-3.5 w-3.5 cursor-pointer rounded border-slate-300 text-[#3366FF] accent-[#3366FF] focus:ring-1 focus:ring-[#3366FF]"
                  />
                  Show all agents
                </label>
              )}
            </div>
            <p className="mb-1.5 text-[11px] text-slate-400">
              {agentsUnscoped
                ? `Campaign and team filters are off — listing all ${agentOptions.length} agents.`
                : 'Pick a campaign — and optionally a team — to list its agents, or tick “Show all agents”.'}
            </p>
            <DualListSelect
              options={agentOptions}
              selected={state.agentIds}
              onChange={(ids) => onChange({ agentIds: ids })}
              availableLabel="Agents"
              selectedLabel="Selected Agents"
              searchPlaceholder="Search agents…"
              height={180}
              emptyText={
                agentsUnscoped
                  ? 'No agents found'
                  : !state.campaignId
                    ? 'Select a campaign to list agents, or tick “Show all agents”'
                    : state.teamId
                      ? 'No agents in this team'
                      : 'No agents in this campaign'
              }
            />
          </div>
        )}

        {/* Filter actions */}
        <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
          {showActions && onApply && agentScopeMissing && (
            <span className="mr-auto text-[11px] text-amber-600">
              Choose a campaign and team, or tick “Show all agents”.
            </span>
          )}
          {showActions && (
            <button
              type="button"
              onClick={onReset}
              className={`inline-flex items-center gap-1.5 ${R} px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100`}
            >
              <RotateCcw className="h-4 w-4" /> Reset
            </button>
          )}
          {showActions && onApply && (
            <button
              type="button"
              onClick={onApply}
              disabled={applying || agentScopeMissing}
              title={agentScopeMissing ? 'Select a campaign and team, or tick “Show all agents”' : undefined}
              className={`inline-flex items-center gap-2 ${R} bg-[#3366FF] px-6 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#2952CC] active:scale-[0.98] disabled:opacity-60`}
            >
              {applying ? <Loader2Spinner /> : <Search className="h-4 w-4" />}
              {applyLabel}
            </button>
          )}
        </div>
      </div>

      <FilterFieldsDialog
        open={fieldsDialogOpen}
        value={selectedFields}
        omit={omittedFields}
        onSave={saveFields}
        onResetDefaults={resetFields}
        onClose={() => setFieldsDialogOpen(false)}
      />
    </div>
  )
}

function Loader2Spinner(): JSX.Element {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}
